/**
 * "Open in the ShowPhaze app" — platform detection, web→app route mapping, and the
 * scheme-with-store-fallback dance.
 *
 * Why a custom scheme here and not a plain https link
 * ---------------------------------------------------
 * Since app 3.3.0, Universal Links / App Links are configured for a few email paths
 * (/login, /onboarding, /shareable/sow, /shareable/po, /show, /show-media,
 * /survey/*, /opportunities): see `public/.well-known/`, `code/mobile/src/lib/app-links.json` (the
 * table, with the steps to add a path) and backend `common/utils/app-link.util.ts`.
 * The OS only hands those over when a link is TAPPED from another app (Mail, Messages).
 * A URL pasted into the browser, a navigation inside this site, or an older app
 * version never opens the app, so this banner still needs `showphaze://…`, and only
 * after an explicit user tap.
 *
 * ⚠️ The app side needs a NEW STORE BUILD for this to work
 * ---------------------------------------------------------
 * `app/(contractor)/_layout.tsx` used to have no `guest` branch — the session gate
 * lived on the root route (`app/index.tsx`), which a deep link skips — so a user not
 * signed into the APP landed on `po-sow` with an empty `contractorId` and got a blank
 * screen: no login prompt, no error. Fixed on 27/07 (guest gate + `?next=` carried
 * through the login, `code/mobile/src/features/auth/nextRoute.ts`), but that fix
 * ships in the app binary. Until the build reaching users includes it, expect the
 * blank screen for anyone signed out of the app.
 *
 * On iOS, tapping this with the app NOT installed shows Safari's "cannot open the
 * page" alert before the store fallback fires. Unavoidable with a custom scheme; it
 * only happens to people who deliberately tapped "Open in app". Android avoids it
 * entirely by going through `intent://` (see below).
 */

export type MobilePlatform = 'ios' | 'android';

const APP_SCHEME = 'showphaze';
const ANDROID_PACKAGE = 'com.app.showphaze';

/**
 * Route path inside the app, with no leading slash.
 *
 * Verified against `expo-router/build/fork/extractPathFromURL.js` (`fromDeepLink`):
 * it concatenates host + pathname, so `showphaze://po-sow` (host "po-sow") and
 * `showphaze:///po-sow` (path "/po-sow") both normalise to the same `po-sow` route.
 * `(contractor)` is a group segment and never appears in the URL, and `po-sow` is a
 * unique route name across the app — no ambiguity.
 */
const APP_HOME_PATH = '';

export const APP_STORE_URL = 'https://apps.apple.com/us/app/showphaze/id6503709618';
export const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.app.showphaze&hl=en_IN';

/**
 * How long to wait for the app to take over before giving up and going to the store.
 *
 * iOS only. 1500ms was too short and produced the "app opens AND the App Store opens"
 * bug: tapping a custom scheme makes Safari show a confirm dialog ("Open in
 * ShowPhaze?"), and while that dialog is up the page is NOT hidden — no
 * `visibilitychange`, `document.hidden` still false. A human takes longer than 1.5s
 * to answer a modal, so the timer fired first and sent Safari to the store; then the
 * app launched too.
 *
 * 4s covers the dialog comfortably. It is a mitigation, not a cure: you cannot
 * distinguish "hasn't answered yet" from "app not installed" with a custom scheme.
 * The residual hole is CANCEL — someone who declines the dialog still gets sent to
 * the store after the timeout, because there is no signal for it. iOS Safari doesn't
 * run this path at all (the native Smart App Banner in index.html handles it), so
 * this only affects Chrome/Firefox on iOS.
 */
const STORE_FALLBACK_MS = 4000;

/** Guards against a double tap firing two competing navigations. */
let handoffInFlight = false;

/**
 * Returns 'ios' | 'android' for a real phone/tablet, or null for desktop.
 * Same iOS regex and iPadOS `maxTouchPoints` trick the existing
 * useQrAppPrompt / useContractorSignupAppPrompt use, but as an allowlist: an
 * unknown user agent yields null instead of being treated as mobile.
 */
export function detectMobilePlatform(): MobilePlatform | null {
  if (typeof navigator === 'undefined') return null;

  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('android')) return 'android';

  const isIOS =
    /iphone|ipad|ipod/.test(ua) || (ua.includes('macintosh') && navigator.maxTouchPoints > 1);
  if (isIOS) return 'ios';

  return null;
}

/**
 * Safari on iOS — the only browser that renders the native Smart App Banner declared
 * in `index.html`. Where it shows, this JS banner stands down: two competing offers
 * for the same thing is noise, and the native one is strictly better (the OS knows
 * whether the app is installed, so there is no timer and no double-open).
 *
 * Chrome / Firefox / Edge on iOS all ship WebKit under a Safari-flavoured UA, so they
 * have to be excluded by their own tokens.
 */
export function isIOSSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIOSDevice =
    /iPhone|iPad|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
  if (!isIOSDevice) return false;

  // CriOS = Chrome, FxiOS = Firefox, EdgiOS = Edge, OPT/OPiOS = Opera.
  const isOtherBrowser = /CriOS|FxiOS|EdgiOS|OPT\/|OPiOS|YaBrowser|DuckDuckGo/.test(ua);
  return !isOtherBrowser && ua.includes('Safari');
}

/**
 * True when the page is already running inside a React Native WebView. The app
 * only ever opens external URLs this way today (WorkMarket), but suggesting
 * "open in the app" from inside the app would be absurd, so it stays guarded.
 */
export function isInsideAppWebView(): boolean {
  return (
    typeof window !== 'undefined' &&
    Boolean((window as { ReactNativeWebView?: unknown }).ReactNativeWebView)
  );
}

export function storeUrlFor(platform: MobilePlatform): string {
  return platform === 'android' ? PLAY_STORE_URL : APP_STORE_URL;
}

/**
 * Maps the current web URL to the equivalent screen path in the app, or '' for
 * "just open the app".
 *
 * Deliberately tiny: only routes whose app counterpart was verified to exist and
 * whose parameters travel in the query string. Anything else opens the app home —
 * a wrong deep link is worse than none, because the app has no handler to recover.
 *
 * `currentUserId` matters because `app/(contractor)/po-sow.tsx` reads
 * `contractorId` from the logged-in user and ignores the URL. Deep-linking someone
 * to a document that belongs to a different contractor would silently show them
 * THEIR document under a banner that promised "this document". Not a data leak —
 * the app scopes by JWT — but a lie, so we degrade to the app home instead.
 */
export function buildAppPath(
  pathname: string,
  search: string,
  currentUserId?: string | null,
): string {
  const params = new URLSearchParams(search || '');
  const eventId = params.get('eventId');
  const contractorId = params.get('contractorId');

  // Work invite link: the contractor's Opportunities tab (no params needed).
  if (pathname === '/opportunities') return 'opportunities';
  if (!eventId) return APP_HOME_PATH;
  // Show Media (wrap-up email link): same screen for every role, scoped by JWT in the app.
  if (pathname === '/show-media') return `show-media?eventId=${encodeURIComponent(eventId)}`;
  // Show check email link: the app's `show` route opens the right screen for the role.
  if (pathname === '/show') return `show?eventId=${encodeURIComponent(eventId)}`;
  if (contractorId && currentUserId && contractorId !== currentUserId) {
    return APP_HOME_PATH;
  }

  const mode = pathname === '/shareable/sow' ? 'sow' : pathname === '/shareable/po' ? 'po' : null;
  if (!mode) return APP_HOME_PATH;

  return `po-sow?eventId=${encodeURIComponent(eventId)}&mode=${mode}`;
}

/** True when this page has a real screen in the app, not just the home fallback. */
export function hasAppScreen(
  pathname: string,
  search: string,
  currentUserId?: string | null,
): boolean {
  return buildAppPath(pathname, search, currentUserId) !== APP_HOME_PATH;
}

/**
 * Android goes through `intent://`, which the OS resolves natively: it opens the
 * app when installed and follows `browser_fallback_url` when not.
 *
 * Plain `location.href = "showphaze://…"` is wrong here — Chrome navigates to its
 * ERR_UNKNOWN_URL_SCHEME page, which *replaces the document*, killing the timer
 * that was supposed to send the user to the Play Store. They'd get an error page
 * and no fallback at all.
 */
function androidIntentUrl(appPath: string): string {
  const fallback = encodeURIComponent(PLAY_STORE_URL);
  return (
    `intent://${appPath}#Intent;scheme=${APP_SCHEME};package=${ANDROID_PACKAGE};` +
    `S.browser_fallback_url=${fallback};end`
  );
}

/**
 * Android `intent://` whose fallback is a WEB page instead of the Play Store — used by
 * pages that work in the browser too (Show Media): app installed → app; not → stays on web.
 */
export function androidIntentUrlWithWebFallback(appPath: string, fallbackUrl: string): string {
  return (
    `intent://${appPath}#Intent;scheme=${APP_SCHEME};package=${ANDROID_PACKAGE};` +
    `S.browser_fallback_url=${encodeURIComponent(fallbackUrl)};end`
  );
}

/** Bare custom-scheme URL for a route in the app (iOS "Open in app" with no store fallback). */
export function appSchemeUrl(appPath: string): string {
  return `${APP_SCHEME}://${appPath}`;
}

/**
 * Tries to hand off to the app, and sends the user to the store if nothing happens.
 *
 * Must be called from a user gesture (a tap) — browsers block scheme navigation
 * otherwise.
 *
 * Only iOS needs the JS timer, and its single stop condition is "the page got
 * hidden", i.e. the app came to the front. There is deliberately no elapsed-time
 * heuristic: iOS's "cannot open the page" alert blocks the JS run loop without
 * changing visibility, so a user who takes a couple of seconds to tap OK would
 * look identical to a successful handoff and lose the store fallback entirely.
 */
export function openInApp({
  pathname,
  search,
  platform,
  currentUserId,
}: {
  pathname: string;
  search: string;
  platform: MobilePlatform;
  currentUserId?: string | null;
}): void {
  if (handoffInFlight) return;
  handoffInFlight = true;

  const appPath = buildAppPath(pathname, search, currentUserId);

  if (platform === 'android') {
    // The OS handles both branches; no timer, nothing to clean up.
    window.setTimeout(() => {
      handoffInFlight = false;
    }, STORE_FALLBACK_MS);
    window.location.href = androidIntentUrl(appPath);
    return;
  }

  let appTookOver = false;
  const markHidden = () => {
    if (document.hidden) appTookOver = true;
  };
  const markPageHide = () => {
    appTookOver = true;
  };

  document.addEventListener('visibilitychange', markHidden);
  window.addEventListener('pagehide', markPageHide);

  window.setTimeout(() => {
    document.removeEventListener('visibilitychange', markHidden);
    window.removeEventListener('pagehide', markPageHide);
    handoffInFlight = false;

    if (appTookOver || document.hidden) return;
    window.location.href = APP_STORE_URL;
  }, STORE_FALLBACK_MS);

  window.location.href = `${APP_SCHEME}://${appPath}`;
}
